"use client";

import React from "react";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";


function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div data-section-id="24371" className="animate-pulse space-y-8 max-w-4xl mx-auto">
        <div className="h-12 bg-muted rounded-sm w-3/4 mx-auto" />
        <div className="h-8 bg-muted rounded-sm w-1/3 mx-auto" />
        <div className="h-[400px] bg-muted rounded-sm w-full" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-sm w-full" />
          <div className="h-4 bg-muted rounded-sm w-full" />
          <div className="h-4 bg-muted rounded-sm w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive mb-4">載入文章時發生錯誤: {error}</p>
        <Button onClick={() => refetch()}>重新嘗試</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">找不到相關文章。</p>
        <Link to="/HomePage">
          <Button variant="link">返回首頁</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const date = post.date ? new Date(post.date).toLocaleDateString("zh-TW", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }) : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="mb-10 text-center">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground mb-6">
          {post.categories && post.categories.length > 0 && (
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {date}
          </span>
          
        </div>

        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-8 leading-tight">
          {post.title}
        </h1>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-sm shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto max-h-[600px] object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
      )}

      {}
      <div 
        className="prose prose-lg dark:prose-invert max-w-none mb-16 text-muted-foreground leading-relaxed mx-auto"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12" />

    </article>
  );
}


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-[400px] bg-muted rounded-sm animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <div key={post.id} data-index={i} className="group h-full">
          <Card className="h-full flex flex-col overflow-hidden border-none shadow-md hover:shadow-lg transition-all duration-300 bg-card rounded-sm">
            <div className="relative h-52 overflow-hidden">
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                  <span className="text-4xl font-bold opacity-20">RS</span>
                </div>
              )}
              <div className="absolute top-4 left-4">
                <span className="bg-background/90 backdrop-blur-sm text-foreground text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                  {post.categories?.[0]?.name || "健康資訊"}
                </span>
              </div>
            </div>
            
            <CardHeader className="pb-2 pt-6">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                <Clock className="h-3 w-3" />
                {post.date ? new Date(post.date).toLocaleDateString() : ""}
              </div>
              <CardTitle className="text-xl font-bold leading-tight group-hover:text-primary transition-colors line-clamp-2">
                <Link to={post.link || "#"}>
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>
            
            <CardContent className="flex-grow">
              <div 
                className="text-muted-foreground text-sm line-clamp-3"
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardContent>
            
            <CardFooter className="pt-0 pb-6">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="ghost" className="w-full justify-between group-hover:bg-primary/5 group-hover:text-primary pl-0 hover:pl-2 transition-all">
                  閱讀全文 <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      ))}
    </div>
  );
}


export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || {};
  const mainQuery = new WP_Query(currentWPQueryParams);

  
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    ignore_sticky: true,
  });

  return (
    <section id="single-post-template-section" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4">
        
        {}
        <PostsProvider wp_query={mainQuery} className="mb-24">
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-4 text-center md:text-left">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-2">相關文章</h2>
              <p className="text-muted-foreground">探索更多專業聽力保健知識與服務案例</p>
            </div>
            <Link to="#">
              <Button variant="outline" className="hidden md:flex rounded-sm">瀏覽所有文章</Button>
            </Link>
          </div>

          {}
          <PostsProvider wp_query={{ per_page: 3, ignore_sticky: true }}>
            <RelatedPostsConsumer />
          </PostsProvider>
          
          <div className="mt-10 md:hidden flex justify-center">
            <Link to="#">
              <Button variant="outline" className="w-full rounded-sm">瀏覽所有文章</Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}